import find from 'lodash/find'
import {Container} from '@freckle/educator-materials/ts/components/container'
import {H2, H3} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import AttachRosterCsv from '@freckle/school/ts/rostering/components/import-roster/attach-rosters/attach-csv'
import type {RosterFileT} from '@freckle/school/ts/rostering/components/import-roster/import-roster-container'
import type {RosterFileTypeT} from '@freckle/school/ts/rostering/components/import-roster/import-roster-container/utils'
import {attachContainer} from './attach-rosters.module.scss'

type Props = {
  files: Array<RosterFileT>
  onAttach: (file: File, fileType: RosterFileTypeT) => void
  onRemove: (fileType: RosterFileTypeT) => void
}

export type AttachedStatusT = 'not-attached' | 'success' | 'error'

export function AttachRosters(props: Props) {
  const {onAttach, onRemove, files} = props
  const {t} = useSafeTranslation()

  return (
    <>
      <H2>{t('ATTACH_ROSTERS_H2_HEADING')}</H2>
      <H3>{t('ATTACH_ROSTERS_H3_DIRECTION')}</H3>
      <Container className={attachContainer}>
        <AttachRosterCsv
          fileType="students"
          onAttach={onAttach}
          onRemove={onRemove}
          attachedStatus={getAttachmentStatus('students', files)}
        />
        <AttachRosterCsv
          fileType="courses"
          onAttach={onAttach}
          onRemove={onRemove}
          attachedStatus={getAttachmentStatus('courses', files)}
        />
        <AttachRosterCsv
          fileType="teachers"
          onAttach={onAttach}
          onRemove={onRemove}
          attachedStatus={getAttachmentStatus('teachers', files)}
        />
        <AttachRosterCsv
          fileType="enrollments"
          onAttach={onAttach}
          onRemove={onRemove}
          attachedStatus={getAttachmentStatus('enrollments', files)}
        />
      </Container>
    </>
  )
}

function getAttachmentStatus(
  fileType: RosterFileTypeT,
  files: Array<RosterFileT>,
): AttachedStatusT {
  const file = find(files, f => f.tag === fileType)
  if (file === undefined) {
    return 'not-attached'
  }

  switch (file.errorStatus) {
    case 'file-too-large':
      return 'error'

    case 'no-error':
      return 'success'

    default:
      throw new Error(`Unrecognized CSV attachment status ${file.errorStatus}`)
  }
}
